
import { Result } from '../data/quizData';

interface QuizResultProps {
  resultKey: string;
  result: Result;
  onRestart: () => void;
}

export default function QuizResult({ resultKey, result, onRestart }: QuizResultProps) {
  const resultColors: Record<string, { bg: string; text: string; icon: string; accent: string }> = {
    A: { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'ri-home-heart-line', accent: 'bg-blue-500' },
    B: { bg: 'bg-green-100', text: 'text-green-700', icon: 'ri-team-line', accent: 'bg-green-500' },
    C: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'ri-rocket-line', accent: 'bg-orange-500' },
    D: { bg: 'bg-red-100', text: 'text-red-700', icon: 'ri-shield-star-line', accent: 'bg-red-500' }
  };

  const colors = resultColors[resultKey] || resultColors.B;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className={`${colors.bg} p-8 text-center`}>
        <div className={`w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
          <i className={`${colors.icon} ${colors.text} text-4xl`}></i>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Tu Perfil: <span className={colors.text}>{result.title}</span>
        </h2>
        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
          {result.description}
        </p>
      </div>

      <div className="p-6 md:p-10">
        {/* Razas Recomendadas */}
        <div className="mb-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-paw-print-line text-accent-500"></i>
            Razas Sugeridas para Ti
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {result.breeds.map((breed, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-100 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={breed.image}
                    alt={breed.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute top-3 left-3 ${colors.accent} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                    Recomendado
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-paw-print-fill text-accent-500"></i>
                    {breed.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {breed.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Consejo España */}
        <div className="mb-10 p-6 bg-secondary-50 rounded-xl border-l-4 border-secondary-500">
          <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
            <i className="ri-lightbulb-line text-secondary-600"></i>
            Consejo para España
          </h4>
          <p className="text-gray-700 leading-relaxed">{result.advice}</p>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-accent-50 to-secondary-50 rounded-xl p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            ¿Quieres ayuda para elegir y educar a tu perro?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/34722131257?text=Hola%2C%20he%20completado%20el%20test%20y%20me%20gustar%C3%ADa%20recibir%20asesoramiento"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-green-500 px-8 py-4 text-base font-semibold text-white shadow-lg hover:bg-green-600 transition-all hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-2 text-xl"></i>
              Hablar por WhatsApp
            </a>
            <a
              href="/contacto"
              className="inline-flex items-center justify-center rounded-full bg-accent-500 px-8 py-4 text-base font-semibold text-white shadow-lg hover:bg-accent-600 transition-all hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2 text-xl"></i>
              Agendar Evaluación
            </a>
          </div>
        </div>

        {/* Restart Button */}
        <div className="text-center">
          <button
            onClick={onRestart}
            className="inline-flex items-center justify-center rounded-full bg-gray-200 px-8 py-3 text-base font-semibold text-gray-700 hover:bg-gray-300 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-restart-line mr-2 text-lg"></i>
            Reiniciar Test
          </button>
        </div>
      </div>
    </div>
  );
}
